// "Previously quoted at…" modal for one supplier SKU, backed by
// GET ums/v1/cms/restaurant-inquiries/products/:productId/quote-history.
// Opened from a history icon next to the price fields in the quoting modals;
// the request fires only on open (enabled: isOpen), never eagerly. History
// rows are durable (they survive soft-deleted inquiries), so nothing links out.

import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@elkaso-app/web-design';
import { InquiryQuoteStatus, useGetProductQuoteHistoryApi, VolumePeriod } from 'apis/restaurant-inquiries';
import { fmt } from 'pages/inquiry-processor/utils/scoring';
import { itemStatusMeta } from 'pages/inquiry-processor/utils/status';
import React, { useState } from 'react';

const PAGE_SIZE = 10;

// Same palette the detail screen uses for items[].quotes[] badges.
const QUOTE_STATUS_COLOR: Record<InquiryQuoteStatus, string> = {
  [InquiryQuoteStatus.ACCEPTED]: 'green',
  [InquiryQuoteStatus.REJECTED]: 'red',
  [InquiryQuoteStatus.PENDING]: 'orange',
};

// BE omits volumePeriod when it equals the default (PER_ORDER).
const VOLUME_PERIOD_LABEL: Record<VolumePeriod, string> = {
  [VolumePeriod.PER_ORDER]: 'Per order',
  [VolumePeriod.ONE_TIME]: 'One time',
  [VolumePeriod.WEEKLY]: 'Weekly',
  [VolumePeriod.MONTHLY]: 'Monthly',
  [VolumePeriod.QUARTERLY]: 'Quarterly',
  [VolumePeriod.ANNUAL]: 'Annual',
};

interface Props {
  // Uncontrolled mode: the modal owns its disclosure and opens on this
  // element's click. Omit it (and pass isOpen/onClose) when the opener lives
  // inside a transient container — e.g. a grid cell's Popover, which closes
  // on focus loss and would unmount a modal mounted in its subtree.
  trigger?: JSX.Element;
  isOpen?: boolean;
  onClose?: () => void;
  // Supplier SKU id (the productId POST .../quotes takes); undefined = no pick yet.
  productId?: number;
  productName?: string;
  // The inquiry open in the UI — its own quotes are hidden from "history".
  excludeInquiryId?: number;
}

export const ProductQuoteHistoryModal = ({
  trigger,
  isOpen: isOpenProp,
  onClose: onCloseProp,
  productId,
  productName,
  excludeInquiryId,
}: Props) => {
  const disclosure = useDisclosure();
  const controlled = isOpenProp !== undefined;
  const isOpen = controlled ? isOpenProp : disclosure.isOpen;
  const onClose = controlled ? onCloseProp ?? (() => undefined) : disclosure.onClose;
  const [page, setPage] = useState(1);

  // Lazy by design: nothing is fetched until the icon is clicked.
  const { data, isLoading, isError } = useGetProductQuoteHistoryApi(
    productId,
    { page, limit: PAGE_SIZE, excludeInquiryId },
    { enabled: isOpen && productId != null }
  );

  // Back to page 1 on every open, whichever mode drove it — the picked SKU
  // may have changed since the last open.
  React.useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, productId]);

  const rows = data?.data ?? [];

  return (
    <>
      {trigger ? React.cloneElement(trigger, { onClick: disclosure.onOpen }) : null}

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '5xl' }} scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Quote History
            {data ? (
              <Text as='span' fontWeight='normal' color='gray.500'>
                {' '}
                ({data.total} quote{data.total !== 1 ? 's' : ''})
              </Text>
            ) : null}
            {productName && (
              <Text fontSize='sm' fontWeight='normal' color='gray.500'>
                {productName}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading ? (
              <Center py='8'>
                <Spinner color='red.500' />
              </Center>
            ) : isError ? (
              <Text fontSize='sm' color='gray.400' py='2'>
                Couldn’t load quote history for this product.
              </Text>
            ) : rows.length === 0 ? (
              <Text fontSize='sm' color='gray.400' py='2'>
                This product has never been quoted before.
              </Text>
            ) : (
              <Box overflowX='auto'>
                <Table size='sm' variant='simple'>
                  <Thead>
                    <Tr>
                      <Th>Quoted</Th>
                      <Th>Restaurant</Th>
                      <Th>Supplier</Th>
                      <Th isNumeric>Price</Th>
                      <Th whiteSpace='nowrap'>Volume</Th>
                      <Th>Item</Th>
                      <Th>Inquiry</Th>
                      <Th>Notes</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((q) => {
                      const item = q.restaurantInquiryItem;
                      const inq = item?.restaurantInquiry;
                      const iMeta = itemStatusMeta(item?.status);
                      return (
                        <Tr key={q.id} fontSize='xs'>
                          <Td whiteSpace='nowrap'>{fmt(new Date(q.createdAt).getTime())}</Td>
                          <Td>{inq?.restaurant?.nameEn || (inq ? `Restaurant — inquiry #${inq.id}` : '—')}</Td>
                          <Td>{q.supplier?.name || `Supplier #${q.supplierId}`}</Td>
                          <Td isNumeric fontWeight='semibold' whiteSpace='nowrap'>
                            {q.price} {q.currency}
                          </Td>
                          <Td whiteSpace='nowrap'>
                            {item?.volume != null ? (
                              <>
                                {item.volume}
                                <Text as='span' color='gray.400' ml='1'>
                                  {VOLUME_PERIOD_LABEL[item.volumePeriod ?? VolumePeriod.PER_ORDER]}
                                </Text>
                              </>
                            ) : (
                              '—'
                            )}
                          </Td>
                          <Td>
                            <Badge colorScheme={QUOTE_STATUS_COLOR[q.status] || 'gray'} variant='subtle'>
                              {q.status}
                            </Badge>
                          </Td>
                          <Td whiteSpace='nowrap'>
                            {item ? (
                              <>
                                <Badge colorScheme={iMeta.colorScheme} variant='subtle'>
                                  {iMeta.label}
                                </Badge>
                                {inq && (
                                  <Text as='span' color='gray.400' ml='1'>
                                    {inq.fulfilledAt ? fmt(new Date(inq.fulfilledAt).getTime()) : 'in progress'}
                                  </Text>
                                )}
                              </>
                            ) : (
                              '—'
                            )}
                          </Td>
                          {/* Full note, wrapped — never truncated. */}
                          <Td color='gray.500' maxW='240px' whiteSpace='normal'>
                            {q.notes || '—'}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
          <ModalFooter gap='2'>
            {data && data.pageCount > 1 && (
              <Flex align='center' gap='2' mr='auto'>
                <Button size='sm' variant='outline' isDisabled={!data.hasPrev} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Text fontSize='sm' color='gray.600' whiteSpace='nowrap'>
                  Page {data.page} of {data.pageCount}
                </Text>
                <Button size='sm' variant='outline' isDisabled={!data.hasNext} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </Flex>
            )}
            <Button variant='outline' colorScheme='gray' onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
